import { useState, useEffect, useCallback } from "react";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Rocket, Plus, ClipboardList, CheckCircle2, Clock, AlertCircle,
  Play, User, FileText
} from "lucide-react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";

const STATUS_STYLE = {
  pending: { badge: "bg-amber-100 text-amber-700", icon: Clock },
  in_progress: { badge: "bg-blue-100 text-blue-700", icon: AlertCircle },
  completed: { badge: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
};

const Onboarding = () => {
  const [templates, setTemplates] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("tasks");
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [showStartDialog, setShowStartDialog] = useState(false);
  const [newTemplate, setNewTemplate] = useState({ name: "", description: "", tasks: [] });
  const [newTask, setNewTask] = useState({ employee_id: "", title: "", description: "", due_date: "", category: "general" });
  const [startForm, setStartForm] = useState({ employee_id: "", template_id: "" });
  const [taskInput, setTaskInput] = useState("");

  const headers = useCallback(() => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const opts = { headers: headers(), withCredentials: true };
      const [tempRes, taskRes, empRes] = await Promise.all([
        axios.get(`${API}/onboarding/templates`, opts).catch(() => ({ data: [] })),
        axios.get(`${API}/onboarding/tasks`, opts).catch(() => ({ data: [] })),
        axios.get(`${API}/employees`, opts).catch(() => ({ data: [] })),
      ]);
      setTemplates(tempRes.data || []);
      setTasks(taskRes.data || []);
      setEmployees(empRes.data || []);
    } catch {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreateTemplate = async () => {
    if (!newTemplate.name) { toast.error("Nombre requerido"); return; }
    try {
      await axios.post(`${API}/onboarding/templates`, newTemplate, { headers: headers(), withCredentials: true });
      toast.success("Plantilla creada");
      setShowTemplateDialog(false);
      setNewTemplate({ name: "", description: "", tasks: [] });
      fetchData();
    } catch { toast.error("Error al crear plantilla"); }
  };

  const handleCreateTask = async () => {
    if (!newTask.employee_id || !newTask.title) { toast.error("Empleado y título requeridos"); return; }
    try {
      await axios.post(`${API}/onboarding/tasks`, newTask, { headers: headers(), withCredentials: true });
      toast.success("Tarea creada");
      setShowTaskDialog(false);
      setNewTask({ employee_id: "", title: "", description: "", due_date: "", category: "general" });
      fetchData();
    } catch { toast.error("Error al crear tarea"); }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      await axios.put(`${API}/onboarding/tasks/${taskId}/complete`, {}, { headers: headers(), withCredentials: true });
      toast.success("Tarea completada");
      fetchData();
    } catch { toast.error("Error al completar tarea"); }
  };

  const handleStartOnboarding = async () => {
    if (!startForm.employee_id) { toast.error("Selecciona un empleado"); return; }
    try {
      const params = new URLSearchParams();
      if (startForm.template_id) params.append("template_id", startForm.template_id);
      await axios.post(`${API}/onboarding/start/${startForm.employee_id}?${params}`, {}, { headers: headers(), withCredentials: true });
      toast.success("Onboarding iniciado");
      setShowStartDialog(false);
      setStartForm({ employee_id: "", template_id: "" });
      fetchData();
    } catch { toast.error("Error al iniciar onboarding"); }
  };

  const addTaskToTemplate = () => {
    if (!taskInput.trim()) return;
    setNewTemplate(prev => ({ ...prev, tasks: [...prev.tasks, { title: taskInput.trim(), category: "general" }] }));
    setTaskInput("");
  };

  // Group tasks by employee
  const groupedTasks = tasks.reduce((acc, task) => {
    const key = task.employee_id || "sin_asignar";
    if (!acc[key]) acc[key] = [];
    acc[key].push(task);
    return acc;
  }, {});

  if (loading) {
    return (
      <Layout title="Onboarding">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout
      title="Onboarding"
      subtitle="Gestiona el proceso de incorporación de nuevos colaboradores"
      actions={
        <div className="flex gap-2">
          <Dialog open={showStartDialog} onOpenChange={setShowStartDialog}>
            <DialogTrigger asChild>
              <Button data-testid="start-onboarding-btn"><Play className="w-4 h-4 mr-2" /> Iniciar Onboarding</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Iniciar Proceso de Onboarding</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Empleado</Label>
                  <Select value={startForm.employee_id} onValueChange={(v) => setStartForm({ ...startForm, employee_id: v })}>
                    <SelectTrigger data-testid="start-employee-select"><SelectValue placeholder="Seleccionar empleado" /></SelectTrigger>
                    <SelectContent>
                      {employees.map(e => (
                        <SelectItem key={e.employee_id} value={e.employee_id}>
                          {e.first_name} {e.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Plantilla (opcional)</Label>
                  <Select value={startForm.template_id} onValueChange={(v) => setStartForm({ ...startForm, template_id: v })}>
                    <SelectTrigger data-testid="start-template-select"><SelectValue placeholder="Sin plantilla" /></SelectTrigger>
                    <SelectContent>
                      {templates.map(t => <SelectItem key={t.template_id} value={t.template_id}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={handleStartOnboarding} className="w-full" data-testid="confirm-start-btn">Iniciar</Button>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={showTaskDialog} onOpenChange={setShowTaskDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="add-task-btn"><Plus className="w-4 h-4 mr-2" /> Tarea</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nueva Tarea de Onboarding</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Empleado</Label>
                  <Select value={newTask.employee_id} onValueChange={(v) => setNewTask({ ...newTask, employee_id: v })}>
                    <SelectTrigger data-testid="task-employee-select"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                    <SelectContent>
                      {employees.map(e => (
                        <SelectItem key={e.employee_id} value={e.employee_id}>{e.first_name} {e.last_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Título</Label>
                  <Input value={newTask.title} onChange={(e) => setNewTask({ ...newTask, title: e.target.value })} data-testid="task-title-input" />
                </div>
                <div>
                  <Label>Descripción</Label>
                  <Textarea rows={2} value={newTask.description} onChange={(e) => setNewTask({ ...newTask, description: e.target.value })} />
                </div>
                <div>
                  <Label>Fecha límite</Label>
                  <Input type="date" value={newTask.due_date} onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })} />
                </div>
                <Button onClick={handleCreateTask} className="w-full" data-testid="save-task-btn">Guardar</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      }
    >
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6" data-testid="onboarding-tabs">
          <TabsTrigger value="tasks"><ClipboardList className="w-4 h-4 mr-2" /> Tareas</TabsTrigger>
          <TabsTrigger value="templates"><FileText className="w-4 h-4 mr-2" /> Plantillas</TabsTrigger>
        </TabsList>

        <TabsContent value="tasks">
          {Object.keys(groupedTasks).length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-slate-500" data-testid="tasks-empty">
                <Rocket className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="font-medium">No hay tareas de onboarding</p>
                <p className="text-sm mt-1">Inicia un proceso de onboarding para un nuevo colaborador</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedTasks).map(([empId, empTasks]) => {
                const emp = employees.find(e => e.employee_id === empId);
                const completed = empTasks.filter(t => t.status === "completed").length;
                const total = empTasks.length;
                const progress = total > 0 ? Math.round((completed / total) * 100) : 0;

                return (
                  <Card key={empId} data-testid={`onboarding-group-${empId}`}>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                            <User className="w-4 h-4 text-emerald-600" />
                          </div>
                          <div>
                            <CardTitle className="text-base">
                              {emp ? `${emp.first_name} ${emp.last_name}` : empId}
                            </CardTitle>
                            <p className="text-xs text-slate-500">{emp?.position}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold text-slate-900">{completed}/{total}</p>
                          <p className="text-xs text-slate-500">completadas</p>
                        </div>
                      </div>
                      <Progress value={progress} className="h-2 mt-3" />
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {empTasks.map((task) => {
                        const style = STATUS_STYLE[task.status] || STATUS_STYLE.pending;
                        const Icon = style.icon;
                        return (
                          <div key={task.task_id} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50" data-testid={`task-${task.task_id}`}>
                            <Icon className="w-4 h-4 shrink-0 text-slate-500" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-900">{task.title || task.name || "Tarea"}</p>
                              {task.description && <p className="text-xs text-slate-500 truncate">{task.description}</p>}
                            </div>
                            <Badge className={style.badge}>{task.status}</Badge>
                            {task.status !== "completed" && (
                              <Button size="sm" variant="outline" onClick={() => handleCompleteTask(task.task_id)} data-testid={`complete-task-${task.task_id}`}>
                                <CheckCircle2 className="w-3 h-3 mr-1" /> Completar
                              </Button>
                            )}
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="templates">
          <div className="flex justify-end mb-4">
            <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="add-onb-template-btn"><Plus className="w-4 h-4 mr-2" /> Nueva Plantilla</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Nueva Plantilla de Onboarding</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Nombre</Label>
                    <Input value={newTemplate.name} onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })} data-testid="onb-template-name" />
                  </div>
                  <div>
                    <Label>Descripción</Label>
                    <Textarea rows={2} value={newTemplate.description} onChange={(e) => setNewTemplate({ ...newTemplate, description: e.target.value })} />
                  </div>
                  <div>
                    <Label>Tareas</Label>
                    <div className="flex gap-2">
                      <Input placeholder="Agregar tarea..." value={taskInput} onChange={(e) => setTaskInput(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && addTaskToTemplate()} data-testid="onb-task-input" />
                      <Button variant="outline" onClick={addTaskToTemplate}>+</Button>
                    </div>
                    {newTemplate.tasks.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {newTemplate.tasks.map((t, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm bg-slate-50 rounded p-2">
                            <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                            <span className="flex-1">{t.title}</span>
                            <button onClick={() => setNewTemplate(prev => ({ ...prev, tasks: prev.tasks.filter((_, idx) => idx !== i) }))}
                              className="text-red-400 hover:text-red-600 text-xs">x</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <Button onClick={handleCreateTemplate} className="w-full" data-testid="save-onb-template-btn">Guardar Plantilla</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {templates.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-slate-500" data-testid="templates-empty">
                <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="font-medium">No hay plantillas</p>
                <p className="text-sm mt-1">Crea plantillas con tareas predefinidas para estandarizar el onboarding</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map((t) => (
                <Card key={t.template_id} className="card-hover" data-testid={`onb-template-${t.template_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Rocket className="w-5 h-5 text-emerald-600" />
                      <p className="font-semibold text-slate-900">{t.name}</p>
                    </div>
                    {t.description && <p className="text-sm text-slate-500 mb-2">{t.description}</p>}
                    <Badge variant="outline">{(t.tasks || []).length} tareas</Badge>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </Layout>
  );
};

export default Onboarding;
